import { useCallback, useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import JobZImage from "../../../common/jobz-img";
import { adminService } from "../../../../services/admin-service";
import { jobService } from "../../../../services/job-service";
import { AdminPageHeader, AdminPanel, EmptyState, StatusBadge, formatDate } from "./admin-ui";
import ApplicantDataTable from "../../../common/applicant-data-table";

const JOB_TYPES = ["Freelance", "Full Time", "Internship", "Part Time", "Temporary", "Volunteer"];

function AdminJobsPage({ status, title }) {
    const [searchParams] = useSearchParams();
    const [jobs, setJobs] = useState([]);
    const [selected, setSelected] = useState(null);
    const [viewingJob, setViewingJob] = useState(null);
    const [viewers, setViewers] = useState([]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);

    // Filter states
    const [filterStatus, setFilterStatus] = useState(status || "");
    const [filterKeyword, setFilterKeyword] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [filterEmployer, setFilterEmployer] = useState(searchParams.get("employerEmail") || searchParams.get("employerName") || "");

    // Bulk selection state
    const [selectedIds, setSelectedIds] = useState([]);

    const load = useCallback(() => {
        setLoading(true);
        adminService.jobs({ status: filterStatus, keyword: filterKeyword })
            .then((data) => {
                setJobs(data.jobs || []);
                setSelectedIds([]); // Reset selection on reload
            })
            .catch((error) => setMessage(error.message))
            .finally(() => setLoading(false));
    }, [filterStatus, filterKeyword]);

    useEffect(() => {
        load();
        jobService.jobCategories()
            .then(data => setCategories(data.categories || []))
            .catch(err => console.error("Failed to load categories", err));
    }, [load]);

    // Client-side filtering for type, category, and employer
    const filteredJobs = useMemo(() => {
        return jobs.filter((job) => {
            const matchesType = !filterType || job.job_type === filterType;
            const matchesCategory = !filterCategory || job.category === filterCategory;
            const matchesEmployer = !filterEmployer || 
                (job.employer_name && job.employer_name.toLowerCase().includes(filterEmployer.toLowerCase())) ||
                (job.employer_email && job.employer_email.toLowerCase().includes(filterEmployer.toLowerCase()));
            return matchesType && matchesCategory && matchesEmployer;
        });
    }, [jobs, filterType, filterCategory, filterEmployer]);

    const act = async (job, action) => {
        const actionLabel = action === "approve" && !job.company_verified
            ? "approve job and approve pending company name"
            : action.replace("-", " ");
        const note = window.prompt(`${actionLabel} note`, "");
        if (note === null) return;
        try {
            if (action === "approve") await adminService.approveJob(job.id, { note });
            if (action === "reject") await adminService.rejectJob(job.id, { reason: note || "Rejected by admin" });
            if (action === "changes") await adminService.requestJobChanges(job.id, { note: note || "Please update this job and resubmit." });
            if (action === "close") await adminService.closeJob(job.id, { note: note || "Closed by admin" });
            setMessage("Job updated successfully.");
            setSelected(null);
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const saveSelected = async () => {
        try {
            await adminService.updateJob(selected.id, selected);
            setMessage("Job edits saved.");
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const [detailType, setDetailType] = useState(""); // "views" or "applied"
    const [detailList, setDetailList] = useState([]);

    const openDetail = async (job, type) => {
        setViewingJob(job);
        setDetailType(type);
        setDetailList([]);
        try {
            if (type === "views") {
                const data = await adminService.jobViews(job.id);
                setDetailList(data.viewers || []);
            } else {
                const data = await adminService.applications({ jobId: job.id });
                setDetailList(data.applications || []);
            }
        } catch (error) {
            setMessage(error.message);
        }
    };

    // Bulk selection handlers
    const toggleSelectAll = () => {
        if (selectedIds.length === filteredJobs.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredJobs.map(j => j.id));
        }
    };

    const toggleSelect = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(x => x !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const runBulkAction = async (action) => {
        if (selectedIds.length === 0) return;

        let note = "";
        if (action === "delete") {
            const confirm = window.confirm(`Are you sure you want to permanently delete these ${selectedIds.length} selected jobs? This cannot be undone.`);
            if (!confirm) return;
        } else if (action === "approve") {
            const confirm = window.confirm(`Approve all ${selectedIds.length} selected jobs?`);
            if (!confirm) return;
        } else {
            note = window.prompt(`Enter note/reason for bulk ${action}:`, "");
            if (note === null) return;
        }

        setLoading(true);
        setMessage(`Running bulk ${action}...`);

        try {
            let successCount = 0;
            for (const jobId of selectedIds) {
                try {
                    if (action === "approve") await adminService.approveJob(jobId, { note });
                    if (action === "reject") await adminService.rejectJob(jobId, { reason: note || "Rejected by admin" });
                    if (action === "changes") await adminService.requestJobChanges(jobId, { note: note || "Changes required." });
                    if (action === "close") await adminService.closeJob(jobId, { note: note || "Closed by admin" });
                    if (action === "delete") await jobService.deleteEmployerJob(jobId);
                    successCount++;
                } catch (err) {
                    console.error(`Failed bulk action ${action} on job ${jobId}:`, err);
                }
            }
            setMessage(`Successfully applied bulk ${action} to ${successCount} of ${selectedIds.length} jobs.`);
            setSelectedIds([]);
            load();
        } catch (error) {
            setMessage(error.message);
            load();
        }
    };

    const liveUrl = (job) => job.live_url || `${window.location.origin}/job-detail/${job.id}`;
    const previewUrl = (job) => job.preview_url || `${window.location.origin}/job-detail/${job.id}?preview=1`;

    return (
        <>
            <AdminPageHeader title={title || "Admin Jobs"} subtitle="Review, edit, and publish jobs" />
            {message && <div className="alert alert-info">{message}</div>}

            {/* Advanced Filters Panel */}
            <AdminPanel title="Filters" icon="fa fa-filter" className="mb-4">
                <div className="row">
                    <div className="col-md-3 m-b15">
                        <label className="form-label">Search Keyword</label>
                        <input 
                            className="form-control" 
                            type="text" 
                            placeholder="Search title, company, or email" 
                            value={filterKeyword} 
                            onChange={(e) => setFilterKeyword(e.target.value)} 
                        />
                    </div>
                    {!status && (
                        <div className="col-md-3 m-b15">
                            <label className="form-label">Status</label>
                            <select 
                                className="form-control" 
                                value={filterStatus} 
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="pending_review">Pending Review</option>
                                <option value="published">Published</option>
                                <option value="rejected">Rejected</option>
                                <option value="needs_changes">Needs Changes</option>
                                <option value="closed">Closed</option>
                            </select>
                        </div>
                    )}
                    <div className="col-md-3 m-b15">
                        <label className="form-label">Job Type</label>
                        <select 
                            className="form-control" 
                            value={filterType} 
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="">All Types</option>
                            {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3 m-b15">
                        <label className="form-label">Category</label>
                        <select 
                            className="form-control" 
                            value={filterCategory} 
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <option value="">All Categories</option>
                            {categories.filter(c => !c.parent_id).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3 m-b15">
                        <label className="form-label">Employer Name / Email</label>
                        <input 
                            className="form-control" 
                            type="text" 
                            placeholder="Filter by Employer Name or Email" 
                            value={filterEmployer} 
                            onChange={(e) => setFilterEmployer(e.target.value)} 
                        />
                    </div>
                </div>
                <div className="text-end">
                    <button 
                        className="btn btn-outline-secondary" 
                        onClick={() => {
                            setFilterKeyword("");
                            if (!status) setFilterStatus("");
                            setFilterType("");
                            setFilterCategory("");
                            setFilterEmployer("");
                        }}
                    >
                        Reset Filters
                    </button>
                </div>
            </AdminPanel>

            {/* Bulk Actions Panel */}
            {selectedIds.length > 0 && (
                <div className="alert alert-dark d-flex align-items-center justify-content-between p-a15 m-b20">
                    <div className="fw-bold">
                        <i className="fa fa-tasks me-2" />
                        {selectedIds.length} jobs selected
                    </div>
                    <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-success fw-bold" onClick={() => runBulkAction("approve")}>
                            <i className="fa fa-check" /> Approve Selected
                        </button>
                        <button className="btn btn-sm btn-warning fw-bold text-dark" onClick={() => runBulkAction("changes")}>
                            <i className="fa fa-edit" /> Request Changes
                        </button>
                        <button className="btn btn-sm btn-danger fw-bold" onClick={() => runBulkAction("reject")}>
                            <i className="fa fa-times" /> Reject Selected
                        </button>
                        <button className="btn btn-sm btn-secondary fw-bold" onClick={() => runBulkAction("close")}>
                            <i className="fa fa-ban" /> Close Selected
                        </button>
                        <button className="btn btn-sm btn-dark fw-bold" onClick={() => runBulkAction("delete")}>
                            <i className="fa fa-trash" /> Delete Selected
                        </button>
                    </div>
                </div>
            )}

            <AdminPanel title="Job Moderation" icon="fa fa-suitcase">
                {loading ? (
                    <EmptyState text="Loading jobs..." />
                ) : (
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover twm-bookmark-list-wrap align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: 40 }} className="text-center">
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input"
                                            checked={filteredJobs.length > 0 && selectedIds.length === filteredJobs.length}
                                            onChange={toggleSelectAll} 
                                        />
                                    </th>
                                    <th>Job Details</th>
                                    <th>Company</th>
                                    <th>Status</th>
                                    <th>Applications / Views</th>
                                    <th>Created</th>
                                    <th>Links</th>
                                    <th className="text-center">Single Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredJobs.length === 0 ? (
                                    <tr><td colSpan="8"><EmptyState text="No jobs found matching criteria." /></td></tr>
                                ) : filteredJobs.map((job, index) => (
                                    <tr key={job.id} className={selectedIds.includes(job.id) ? "table-primary" : ""}>
                                        <td className="text-center">
                                            <input 
                                                type="checkbox" 
                                                className="form-check-input"
                                                checked={selectedIds.includes(job.id)} 
                                                onChange={() => toggleSelect(job.id)} 
                                            />
                                        </td>
                                        <td>
                                            <div className="twm-bookmark-list">
                                                <div className="twm-media">
                                                    <div className="twm-media-pic">
                                                        <JobZImage src={job.job_image_url || job.company_logo_url || `images/jobs-company/pic${(index % 5) + 1}.jpg`} alt={job.title} />
                                                    </div>
                                                </div>
                                                <div className="twm-mid-content">
                                                    <button type="button" className="btn p-0 text-start twm-job-title" onClick={() => setSelected(job)}>
                                                        <h4 className="mb-1">{job.title}</h4>
                                                        <p className="twm-bookmark-address mb-1"><i className="feather-map-pin" />{job.location}</p>
                                                    </button>
                                                    <div className="mt-1">
                                                        <span className="badge bg-secondary me-2">{job.job_type}</span>
                                                        <span className="badge bg-light text-dark">{job.category}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="fw-bold text-dark">{job.company_name || "Company"}</div>
                                            <div className="text-muted small mt-1">
                                                <div><i className="fa fa-user me-1 text-primary" />{job.employer_name || "N/A"}</div>
                                                <div><i className="fa fa-envelope me-1 text-primary" />{job.employer_email || "N/A"}</div>
                                            </div>
                                            {job.company_name && (
                                                <div className="m-t5">
                                                    <span className={`badge ${job.company_verified ? "bg-success" : "bg-danger"}`}>
                                                        {job.company_verified ? "Company Approved" : "Company Name Not Approved"}
                                                    </span>
                                                </div>
                                            )}
                                            {!job.company_verified && (
                                                <small className="text-danger d-block m-t5" style={{ fontSize: "10px" }}>Approving this job will approve this company.</small>
                                            )}
                                        </td>
                                        <td><StatusBadge status={job.status} /></td>
                                        <td>
                                            <button className="btn btn-sm btn-link p-0 fw-bold d-block text-start mb-1" type="button" onClick={() => openDetail(job, "applied")}>
                                                {job.application_count || 0} Applied
                                            </button>
                                            <button className="btn btn-sm btn-link p-0 d-block text-start" type="button" onClick={() => openDetail(job, "views")}>
                                                {job.view_count || 0} Views
                                            </button>
                                        </td>
                                        <td>{formatDate(job.created_at)}</td>
                                        <td>
                                            <div className="d-flex flex-column gap-1">
                                                <a className="site-text-primary small fw-bold" href={previewUrl(job)} target="_blank" rel="noreferrer">
                                                    <i className="fa fa-eye" /> Preview Job
                                                </a>
                                                {job.status === "published" ? (
                                                    <a className="text-success small fw-bold" href={liveUrl(job)} target="_blank" rel="noreferrer">
                                                        <i className="fa fa-external-link-alt" /> Live Job
                                                    </a>
                                                ) : (
                                                    <small className="text-warning small">Draft Preview Only</small>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1 justify-content-center">
                                                <button className="btn btn-sm btn-warning text-dark fw-bold" type="button" onClick={() => setSelected(job)} title="Edit">
                                                    <i className="fa fa-edit" /> Edit
                                                </button>
                                                {job.status !== "published" && (
                                                    <button className="btn btn-sm btn-success" type="button" onClick={() => act(job, "approve")}>
                                                        Approve
                                                    </button>
                                                )}
                                                {job.status !== "needs_changes" && (
                                                    <button className="btn btn-sm btn-outline-warning" type="button" onClick={() => act(job, "changes")}>
                                                        Changes
                                                    </button>
                                                )}
                                                {job.status !== "rejected" && (
                                                    <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => act(job, "reject")}>
                                                        Reject
                                                    </button>
                                                )}
                                                {job.status === "published" && (
                                                    <button className="btn btn-sm btn-secondary" type="button" onClick={() => act(job, "close")}>
                                                        Close
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </AdminPanel>

            {/* Edit Panel */}
            {selected && (
                <AdminPanel title="Edit Selected Job" icon="fa fa-edit" className="mt-4">
                    <div className="row">
                        <TextField label="Title" value={selected.title} onChange={(title) => setSelected({ ...selected, title })} />
                        <div className="col-md-6 m-b20">
                            <label className="form-label">Category</label>
                            <select 
                                className="form-control" 
                                value={selected.category || ""} 
                                onChange={(e) => setSelected({ ...selected, category: e.target.value, subcategory: "" })}
                            >
                                <option value="">Select Category</option>
                                {categories.filter(c => !c.parent_id).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                            </select>
                        </div>
                        <div className="col-md-6 m-b20">
                            <label className="form-label">Subcategory</label>
                            <select 
                                className="form-control" 
                                value={selected.subcategory || ""} 
                                onChange={(e) => setSelected({ ...selected, subcategory: e.target.value })}
                            >
                                <option value="">Select Subcategory</option>
                                {categories.filter(c => c.parent_id && categories.find(p => p.id === c.parent_id)?.name === selected.category).map(c => (
                                    <option key={c.id} value={c.name}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <SelectField label="Job Type" value={selected.job_type} onChange={(job_type) => setSelected({ ...selected, job_type })} options={JOB_TYPES} />
                        <TextField label="Location" value={selected.location} onChange={(location) => setSelected({ ...selected, location })} />
                        <TextField label="Salary Min (INR)" type="number" value={selected.salary_min || ""} onChange={(salary_min) => setSelected({ ...selected, salary_min })} />
                        <TextField label="Salary Max (INR)" type="number" value={selected.salary_max || ""} onChange={(salary_max) => setSelected({ ...selected, salary_max })} />
                        <TextField label="Workplace Type" value={selected.workplace_type} onChange={(workplace_type) => setSelected({ ...selected, workplace_type })} />
                        <TextField label="Experience" value={selected.experience || ""} onChange={(experience) => setSelected({ ...selected, experience })} />
                        <TextField label="Qualification" value={selected.qualification || ""} onChange={(qualification) => setSelected({ ...selected, qualification })} />
                        <TextField label="Expiry Date" value={selected.expires_at || ""} onChange={(expires_at) => setSelected({ ...selected, expires_at })} />
                        <TextField label="Support Email" value={selected.support_email || ""} onChange={(support_email) => setSelected({ ...selected, support_email })} />
                        <TextField label="Support Phone Number" value={selected.support_phone || ""} onChange={(support_phone) => setSelected({ ...selected, support_phone })} />
                        <div className="col-md-12 m-b20">
                            <label className="d-block">
                                <input 
                                    type="checkbox" 
                                    checked={Boolean(selected.mask_contact_details)} 
                                    onChange={(event) => setSelected({ ...selected, mask_contact_details: event.target.checked })} 
                                    className="form-check-input me-2"
                                /> 
                                Mask public contact details
                            </label>
                        </div>
                        <div className="col-md-12 m-b20">
                            <div className={`alert ${selected.company_verified ? "alert-success" : "alert-warning"}`}>
                                <strong>Company:</strong> {selected.company_name || "Company"} — {selected.company_verified ? "Approved company" : "Company name is not approved yet. Approving this job will also approve this company name."}
                            </div>
                        </div>
                        <div className="col-md-12">
                            <label className="form-label">Description</label>
                            <textarea className="form-control" rows="5" value={selected.description || ""} onChange={(event) => setSelected({ ...selected, description: event.target.value })} />
                        </div>
                    </div>
                    <div className="m-t20">
                        <a className="site-button me-2" href={previewUrl(selected)} target="_blank" rel="noreferrer">Open Draft Preview</a>
                        {selected.status === "published" && <a className="site-button me-2" href={liveUrl(selected)} target="_blank" rel="noreferrer">Open Live Job</a>}
                        <button className="site-button me-2" type="button" onClick={saveSelected}>Save Edits</button>
                        <button className="site-button-secondry" type="button" onClick={() => setSelected(null)}>Cancel</button>
                    </div>
                </AdminPanel>
            )}

            {/* Candidates List Panel */}
            {viewingJob && (
                <AdminPanel 
                    title={detailType === "views" ? `Candidate Views: ${viewingJob.title}` : `Job Applicants: ${viewingJob.title}`} 
                    icon={detailType === "views" ? "fa fa-eye" : "fa fa-file-alt"} 
                    className="mt-4"
                >
                    <ApplicantDataTable 
                        applications={detailList} 
                        title={detailType === "views" ? `${viewingJob.title} Viewers` : `${viewingJob.title} Applicants`} 
                        emptyText={detailType === "views" ? "No logged-in candidate views recorded yet." : "No candidates have applied for this job yet."} 
                    />
                    <button className="site-button-secondry m-t15" type="button" onClick={() => { setViewingJob(null); setDetailType(""); setDetailList([]); }}>Close List</button>
                </AdminPanel>
            )}
        </>
    );
}

function TextField({ label, value, onChange, type = "text" }) {
    return (
        <div className="col-md-6 m-b20">
            <label className="form-label">{label}</label>
            <input className="form-control" type={type} value={value || ""} onChange={(event) => onChange(event.target.value)} />
        </div>
    );
}

function SelectField({ label, value, onChange, options }) {
    return (
        <div className="col-md-6 m-b20">
            <label className="form-label">{label}</label>
            <select className="form-control" value={value || ""} onChange={(event) => onChange(event.target.value)}>
                {options.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
        </div>
    );
}

export default AdminJobsPage;
